import { useCallback, useEffect, useRef, useState } from 'react';
import type { ResultOption } from '@/apis/core/search/whatToDo/post/postWhatToDo';
import {
  QUESTION_ASSISTANT_HISTORY_STORAGE_KEY,
  QUESTION_ASSISTANT_MAX_HISTORY_ITEMS,
} from './Landingpage.constants';
import type {
  LandingPageHistoryItem,
  QuestionAnswer,
} from './Landingpage.types';
import { parseHistoryFromStorage } from './Landingpage.utils';

export const useLandingPageHistory = () => {
  const [historyItems, setHistoryItems] = useState<LandingPageHistoryItem[]>(
    [],
  );
  const lastResultOptionsForHistoryRef = useRef<ResultOption[]>([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(QUESTION_ASSISTANT_HISTORY_STORAGE_KEY);
      setHistoryItems(parseHistoryFromStorage(raw));
    } catch {
      // ignore invalid stored data
    }
  }, []);

  useEffect(() => {
    if (historyItems.length === 0) return;
    try {
      localStorage.setItem(
        QUESTION_ASSISTANT_HISTORY_STORAGE_KEY,
        JSON.stringify(historyItems),
      );
    } catch {
      // ignore quota or other errors
    }
  }, [historyItems]);

  const captureResultOptionsForHistory = useCallback((options: ResultOption[]) => {
    lastResultOptionsForHistoryRef.current = options;
  }, []);

  const clearCapturedResultOptions = useCallback(() => {
    lastResultOptionsForHistoryRef.current = [];
  }, []);

  const appendAnswerToHistory = useCallback((answer: QuestionAnswer) => {
    const resultOptions = lastResultOptionsForHistoryRef.current;
    const newItem: LandingPageHistoryItem = {
      id: answer.id,
      question: answer.question,
      answer: answer.answer,
      timestamp: answer.timestamp.getTime(),
      resultOptions: resultOptions.length > 0 ? resultOptions : undefined,
    };
    setHistoryItems((prev) =>
      [newItem, ...prev].slice(0, QUESTION_ASSISTANT_MAX_HISTORY_ITEMS),
    );
    lastResultOptionsForHistoryRef.current = [];
  }, []);

  return {
    historyItems,
    lastResultOptionsForHistoryRef,
    captureResultOptionsForHistory,
    clearCapturedResultOptions,
    appendAnswerToHistory,
  };
};
